import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../context/PermissionContext';
import { 
  Users, Shield, UserX, Save, X, Check, UserPlus, 
  User, AlertCircle, Info, RefreshCw, Eye, Search,
  Plus, PlusCircle, Trash, Lock, Filter
} from 'lucide-react';

const RoleAssignment = () => {
  const { hasPermission } = usePermission();
  const { currentUser } = useAuth();
  
  // API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 
    (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');
  
  // State for users and roles
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [userLoadError, setUserLoadError] = useState(null);
  const [roleLoadError, setRoleLoadError] = useState(null);
  
  // State for selected user
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loadingUserRoles, setLoadingUserRoles] = useState(false);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [editedRoles, setEditedRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedMessage, setSavedMessage] = useState(null);
  
  // Fetch users from API
  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUserLoadError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(data.users);
      setLoadingUsers(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUserLoadError('Failed to load users. Please try again.');
      setLoadingUsers(false);
    }
  };
  
  // Fetch roles from API
  const fetchRoles = async () => {
    setLoadingRoles(true);
    setRoleLoadError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const data = await response.json();
      setRoles(data.roles);
      setLoadingRoles(false);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoleLoadError('Failed to load roles. Please try again.');
      setLoadingRoles(false);
    }
  };
  
  // Fetch user roles
  const fetchUserRoles = async (userId) => {
    setLoadingUserRoles(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/roles/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const data = await response.json();
      setUserRoles(data.roles);
      setEditedRoles(data.roles.map(r => r.id));
      setLoadingUserRoles(false);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setUserRoles([]);
      setEditedRoles([]);
      setLoadingUserRoles(false);
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);
  
  // If the user doesn't have the manage_roles permission, show permission denied
  if (!hasPermission('manage_roles')) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-8 text-center">
        <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-200 mb-2">Permission Denied</h2>
        <p className="text-slate-400 mb-6">You don't have access to manage role assignments.</p>
        <p className="text-slate-500 text-sm">This action requires the 'manage_roles' permission.</p>
      </div>
    );
  }
  
  // Handle user selection
  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    setIsEditingRoles(false);
    setSavedMessage(null);
    fetchUserRoles(userId);
  };
  
  // Handle role selection toggle
  const handleRoleToggle = (roleId) => {
    if (editedRoles.includes(roleId)) {
      setEditedRoles(editedRoles.filter(id => id !== roleId));
    } else {
      setEditedRoles([...editedRoles, roleId]);
    }
  };
  
  // Handle save changes
  const handleSaveChanges = async () => {
    if (editedRoles.length === 0) {
      setSavedMessage("A user must have at least one role.");
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get current roles to compare
      const currentRoleIds = userRoles.map(r => r.id);
      
      // Determine which roles to add and which to remove
      const rolesToAdd = editedRoles.filter(id => !currentRoleIds.includes(id));
      const rolesToRemove = currentRoleIds.filter(id => !editedRoles.includes(id));
      
      // Create an array of promises for all operations
      const operations = [];
      
      // Add roles
      for (const roleId of rolesToAdd) {
        operations.push(
          fetch(`${API_URL}/roles/users/${selectedUser}/roles/${roleId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
          }).then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error: ${response.status}`);
            }
            return response.json();
          })
        );
      }
      
      // Remove roles
      for (const roleId of rolesToRemove) {
        operations.push(
          fetch(`${API_URL}/roles/users/${selectedUser}/roles/${roleId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }).then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error: ${response.status}`);
            }
            return response.json();
          })
        );
      }
      
      // Execute all operations
      await Promise.all(operations);
      
      // Update UI
      setIsEditingRoles(false);
      setSavedMessage("Role assignments updated successfully!");
      
      // Refresh user roles
      fetchUserRoles(selectedUser);
      
      // Clear the success message after 3 seconds
      setTimeout(() => {
        setSavedMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating user roles:', error);
      setSavedMessage("Error updating role assignments. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Get filtered users based on search query
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.name && user.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  // Get color class for role badge
  const getRoleColorClass = (role) => {
    if (!role || !role.color) {
      return 'bg-blue-900/30 text-blue-300 border-blue-800/50';
    }
    
    switch (role.color) {
      case 'red':
        return 'bg-red-900/30 text-red-300 border-red-800/50';
      case 'yellow':
        return 'bg-yellow-900/30 text-yellow-300 border-yellow-800/50';
      case 'green':
        return 'bg-green-900/30 text-green-300 border-green-800/50';
      case 'blue':
      default:
        return 'bg-blue-900/30 text-blue-300 border-blue-800/50';
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <UserPlus className="w-6 h-6 text-blue-400 mr-3" />
            <h2 className="text-xl font-semibold text-slate-200">Role Assignment</h2>
          </div>
        </div>
        <p className="text-slate-400 mt-2">Assign roles to users to control their access to different features.</p>
      </div>
      
      {/* Main content area */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Users list column */}
        <div className="lg:w-1/3">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm h-full">
            <div className="p-4 border-b border-slate-700">
              <div>
                <h3 className="text-md font-medium text-slate-200">Users</h3>
                <p className="text-sm text-slate-400 mt-1">Select a user to manage their roles</p>
              </div>
              
              {/* Search input */}
              <div className="mt-3 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  className="w-full bg-slate-700 border border-slate-600 rounded-md py-2 pl-10 pr-4 text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <div className="p-4 space-y-3 h-[calc(100vh-320px)] overflow-y-auto">
              {loadingUsers ? (
                <div className="flex justify-center items-center h-40">
                  <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
                </div>
              ) : userLoadError ? (
                <div className="p-4 bg-red-900/30 border border-red-800 rounded-lg text-center">
                  <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-red-300">{userLoadError}</p>
                  <button 
                    onClick={fetchUsers}
                    className="mt-3 px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 text-sm"
                  >
                    Try Again
                  </button>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-4 bg-slate-750 border border-slate-700 rounded-lg text-center">
                  <User className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400">No users found</p>
                </div>
              ) : (
                filteredUsers.map(user => (
                  <div 
                    key={user.id}
                    onClick={() => handleUserSelect(user.id)}
                    className={`p-4 rounded-lg cursor-pointer transition-colors
                      ${selectedUser === user.id ? 'bg-blue-900/30 border border-blue-700' : 'hover:bg-slate-750 bg-slate-750/50 border border-transparent'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <span className="font-medium text-slate-200">{user.username}</span>
                          {user.is_active === 0 && (
                            <span className="ml-2 text-xs bg-red-900/30 text-red-300 border border-red-800/50 px-1.5 py-0.5 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{user.email}</p>
                        <p className="text-xs text-slate-500 mt-1">{user.name || 'No name provided'}</p>
                      </div>
                      {selectedUser === user.id && (
                        <div className="bg-blue-500 rounded-full p-1">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        
        {/* Roles column */}
        <div className="lg:w-2/3">
          {selectedUser ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
              <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                <div>
                  <h3 className="text-md font-medium text-slate-200">
                    User Roles
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    {users.find(u => u.id === selectedUser)?.username}'s role assignments
                  </p>
                </div>
                
                {hasPermission('manage_roles') && !loadingUserRoles && (
                  <div>
                    {isEditingRoles ? (
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            setIsEditingRoles(false);
                            setEditedRoles(userRoles.map(r => r.id));
                          }}
                          className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={handleSaveChanges}
                          className="flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg gap-1"
                          disabled={loading}
                        >
                          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          <span>Save Changes</span>
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={() => setIsEditingRoles(true)}
                        className="flex items-center px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg gap-1"
                      >
                        <UserPlus className="w-4 h-4" />
                        <span>Edit Roles</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Saved message */}
              {savedMessage && (
                <div className={`mx-4 mt-4 p-3 rounded-md flex items-center ${
                  savedMessage.includes("Error") 
                    ? "bg-red-900/30 border border-red-800 text-red-300" 
                    : "bg-green-900/30 border border-green-800 text-green-300"
                }`}>
                  {savedMessage.includes("Error") 
                    ? <AlertCircle className="w-4 h-4 mr-2" />
                    : <Check className="w-4 h-4 mr-2" />
                  }
                  {savedMessage}
                </div>
              )}
              
              {/* Loading state */}
              {loadingUserRoles && (
                <div className="flex justify-center items-center p-8">
                  <div className="text-center">
                    <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
                    <p className="text-slate-400">Loading user roles...</p>
                  </div>
                </div>
              )}
              
              {/* Role management */}
              {!loadingUserRoles && (
                <div className="p-4">
                  {loadingRoles ? (
                    <div className="flex justify-center items-center h-40">
                      <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
                    </div>
                  ) : roleLoadError ? (
                    <div className="p-4 bg-red-900/30 border border-red-800 rounded-lg text-center">
                      <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                      <p className="text-red-300">{roleLoadError}</p>
                      <button 
                        onClick={fetchRoles}
                        className="mt-3 px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 text-sm"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <div>
                      {/* Current roles section */}
                      {!isEditingRoles && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-slate-300 mb-3">Current Roles</h4>
                          {userRoles.length === 0 ? (
                            <div className="p-4 bg-slate-750 border border-slate-700 rounded-lg text-center">
                              <UserX className="w-6 h-6 text-slate-500 mx-auto mb-2" />
                              <p className="text-slate-400">No roles assigned</p>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {userRoles.map(role => (
                                <div key={role.id} className={`px-3 py-2 rounded-lg border ${getRoleColorClass(role)}`}>
                                  <div className="flex items-center">
                                    <Shield className="w-4 h-4 mr-2" />
                                    <span>{role.name}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Role editing section */}
                      {isEditingRoles && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-300 mb-3">Assign Roles</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {roles.map(role => {
                              const isAssigned = editedRoles.includes(role.id);
                              
                              return (
                                <div 
                                  key={role.id}
                                  className={`p-3 border rounded-lg cursor-pointer ${
                                    isAssigned 
                                      ? 'bg-blue-900/20 border-blue-700/50' 
                                      : 'bg-slate-800 border-slate-700'
                                  }`}
                                  onClick={() => handleRoleToggle(role.id)}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getRoleColorClass(role)}`}>
                                          {role.name}
                                        </span>
                                        <div 
                                          className="ml-2 text-slate-400 cursor-help"
                                          title={role.description}
                                        >
                                          <Info className="w-3 h-3" />
                                        </div>
                                      </div>
                                      <p className="text-xs text-slate-400 mt-1">{role.description}</p>
                                    </div>
                                    
                                    <div className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                                      isAssigned
                                        ? 'bg-blue-600 border-blue-500'
                                        : 'bg-slate-700 border-slate-600'
                                    }`}>
                                      {isAssigned && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-8 flex flex-col items-center justify-center h-full">
              <User className="w-16 h-16 text-slate-600 mb-4" />
              <h3 className="text-xl font-medium text-slate-300 mb-2">Select a User</h3>
              <p className="text-slate-400 text-center max-w-md">
                Choose a user from the left panel to view and manage their role assignments
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleAssignment;